import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appActions } from '../store';

/**
 * Custom hook to sync Redux state with URL
 * URL format: /pattern/{patternId}/colors/{colorIds}/size/{size}
 * Example: /pattern/6149649/colors/6152720,6152721,6152723/size/8x11
 */
export function useUrlSync() {
    const dispatch = useDispatch();
    const chosenPattern = useSelector(state => state.app.chosenPattern);
    const patternColors = useSelector(state => state.app.patternColors);
    const size = useSelector(state => state.app.size);
    const seriesData = useSelector(state => state.app.seriesData);
    
    const isInitialized = useRef(false);
    const lastUrl = useRef('');

    // Load state from URL on mount
    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        const path = window.location.pathname;
        if (path === '/' || path === '') return;

        const params = parseUrlPath(path);
        
        // If we have a pattern ID in URL, we need to load it
        if (params.patternId) {
            // Store URL params to be processed after series data loads
            window.__pendingUrlParams = params;
        }
    }, []);

    // Process pending URL params after series data loads
    useEffect(() => {
        const params = window.__pendingUrlParams;
        if (!params || !seriesData[9756] || !seriesData[8623]) return;

        // Find the pattern by ID
        const patternId = parseInt(params.patternId, 10);
        const patterns = Object.values(seriesData[9756] || {});
        const pattern = patterns.find(p => p.id === patternId);

        if (pattern) {
            dispatch(appActions.setChosenPattern(pattern));
            dispatch(appActions.setStyle(pattern.name || pattern.color || 'Selected'));

            // If we have colors in URL, apply them
            if (params.colors && params.colors.length > 0) {
                const colorData = seriesData[8623] || {};
                const colorIds = params.colors;
                
                // Build pattern colors from URL
                const SLOT_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
                // Get original IDs from pattern warranty
                const warrantyIds = pattern.warranty?.split(',').map(i => i.trim()) || [];
                
                const colors = colorIds.map((id, index) => {
                    const colorProduct = colorData[id];
                    const originalId = warrantyIds[index] || id;
                    
                    return {
                        index,
                        originalId,
                        currentId: id,
                        label: SLOT_LABELS[index] || `${index + 1}`,
                        sku: colorProduct?.sku || `#${id}`,
                        product: colorProduct || null
                    };
                });
                
                dispatch(appActions.setPatternColors(colors));
            }

            // If we have size in URL, apply it
            if (params.size) {
                dispatch(appActions.setSize(params.size));
            }
        }

        // Clear pending params
        delete window.__pendingUrlParams;
    }, [seriesData, dispatch]);

    // Update URL when state changes
    useEffect(() => {
        // Don't update URL if no pattern is chosen
        if (!chosenPattern) {
            if (lastUrl.current !== '/' && window.location.pathname !== '/') {
                lastUrl.current = '/';
                window.history.replaceState({}, '', '/');
            }
            return;
        }

        // Build color string from patternColors currentId values
        const colorString = patternColors.length > 0
            ? patternColors.map(c => c.currentId).join(',')
            : '';

        const newUrl = buildUrlPath(chosenPattern.id, colorString, size);

        if (lastUrl.current !== newUrl) {
            lastUrl.current = newUrl;
            window.history.replaceState({}, '', newUrl);
        }
    }, [chosenPattern, patternColors, size]);
}

/**
 * Parse URL path into params object
 * /pattern/6149649/colors/6152720,6152721/size/8x11
 */
function parseUrlPath(path) {
    const params = {};
    const parts = path.replace(/^\//, '').split('/');

    for (let i = 0; i < parts.length; i += 2) {
        const key = parts[i];
        const value = parts[i + 1];

        if (key && value) {
            if (key === 'pattern') {
                params.patternId = value;
            } else if (key === 'colors') {
                params.colors = value.split(',').map(id => id.trim());
            } else if (key === 'size') {
                params.size = decodeURIComponent(value);
            }
        }
    }

    return params;
}

/**
 * Build URL path from state
 * /pattern/6149649/colors/6152720,6152721/size/8x11
 */
function buildUrlPath(patternId, colorString, size) {
    const parts = ['pattern', patternId];

    if (colorString) {
        parts.push('colors', colorString);
    }

    if (size) {
        parts.push('size', encodeURIComponent(size));
    }

    return '/' + parts.join('/');
}
